import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import {
  BackButtonComponent,
  PageHeaderComponent,
} from '../../shared/components/ui-shell.component';
import { StationHistoryBarComponent } from '../../shared/components/timeline.component';
import { MonitoringService } from '../../services/monitoring.service';
import { HistoryRange, StationHistorySlot } from '../../models/monitoring.models';
import {
  HISTORY_DAY_OPTIONS,
  HistoryDay,
  currentRangeFor,
  defaultHistorySlotMinutes,
  defaultRangeLabelForDay,
} from '../../utils/history-ranges';

@Component({
  selector: 'app-station-history',
  standalone: true,
  imports: [FormsModule, PageHeaderComponent, BackButtonComponent, StationHistoryBarComponent],
  template: `
    <div class="screen">
      <app-page-header title="Etat de la gare" [subtitle]="stationName()" />

      <section class="content">
        <h2 class="section-title">Historique</h2>

        <label class="range-label">
          Jour
          <select
            class="range-select"
            [ngModel]="selectedDay()"
            (ngModelChange)="onDayChange($event)"
          >
            @for (option of dayOptions; track option.value) {
              <option [ngValue]="option.value">{{ option.label }}</option>
            }
          </select>
        </label>

        <label class="range-label">
          Sélectionner une plage horaire
          <select
            class="range-select"
            [ngModel]="selectedRange()"
            (ngModelChange)="onRangeChange($event)"
          >
            @for (range of ranges(); track range.label) {
              <option [ngValue]="range.label">{{ range.label }}</option>
            }
          </select>
        </label>

        <!-- Créneaux de 15 min sur une fenêtre de 6h, du plus récent (en haut)
             au plus ancien — l'ordre est fourni par le back. -->
        <div class="history-list">
          @for (slot of history(); track slot.minutes) {
            <app-station-history-bar
              [time]="slot.time"
              [color]="slot.color"
              [selected]="slot.minutes === selectedMinutes()"
              (rowClick)="selectSlot(slot)"
            />
          }
        </div>

        <aside class="legend">
          <p><span class="dot green"></span> Tous les DAT de la gare fonctionnent</p>
          <p>
            <span class="dot orange"></span> Au moins un DAT partiellement HS (organe non
            bloquant)
          </p>
          <p><span class="dot red"></span> Au moins un DAT HS (organe bloquant)</p>
          <p><span class="dot grey"></span> Pas de données disponibles</p>
        </aside>
      </section>

      <app-back-button [backFn]="goBack.bind(this)" />
    </div>
  `,
  styles: [
    `
      .screen {
        min-height: 100%;
        display: flex;
        flex-direction: column;
      }

      .content {
        padding: 0 1.25rem 1rem;
        flex: 1;
      }

      .section-title {
        font-size: 1.1rem;
        font-weight: 700;
        margin: 0 0 0.75rem;
      }

      .range-label {
        display: block;
        font-size: 0.85rem;
        color: #555;
        margin-bottom: 1rem;
      }

      .range-select {
        display: block;
        width: 100%;
        margin-top: 0.35rem;
        padding: 0.5rem 0.75rem;
        border: 1px solid #888;
        border-radius: 6px;
        font-size: 0.95rem;
        background: #fff;
        color: #222;
      }

      .history-list {
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
        margin-bottom: 1.5rem;
        max-height: 60vh;
        overflow-y: auto;
      }

      .legend {
        font-size: 0.75rem;
        color: #666;
        border-top: 1px solid #eee;
        padding-top: 0.75rem;
      }

      .legend p {
        margin: 0.25rem 0;
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .dot {
        width: 0.75rem;
        height: 0.75rem;
        border-radius: 50%;
        flex-shrink: 0;
      }

      .dot.green {
        background: #43a047;
      }
      .dot.orange {
        background: #fb8c00;
      }
      .dot.red {
        background: #e53935;
      }
      .dot.grey {
        background: #bdbdbd;
      }
    `,
  ],
})
export class StationHistoryComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly monitoring = inject(MonitoringService);

  readonly dayOptions = HISTORY_DAY_OPTIONS;

  readonly stationId = signal('');
  readonly stationName = signal('');
  readonly ranges = signal<HistoryRange[]>([]);
  readonly history = signal<StationHistorySlot[]>([]);
  readonly selectedMinutes = signal<number | null>(null);
  readonly selectedDay = signal<HistoryDay>('today');
  readonly selectedRange = signal('');

  constructor() {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('stationId') ?? '';
      this.stationId.set(id);
      this.monitoring.getStation(id).subscribe((station) => {
        this.stationName.set(station?.name ?? id);
      });
      this.loadRanges(this.selectedDay());
    });
  }

  onDayChange(day: HistoryDay): void {
    this.selectedDay.set(day);
    this.loadRanges(day);
  }

  onRangeChange(rangeLabel: string): void {
    this.selectedRange.set(rangeLabel);
    const range = this.ranges().find((r) => r.label === rangeLabel);
    if (range) {
      this.loadHistoryForRange(range);
    }
  }

  /**
   * Les plages de 6h viennent du back (source unique de vérité). Pour aujourd'hui, on
   * sélectionne celle dont la fenêtre [startMinutes, endMinutes) — en minutes UTC de la
   * journée, comme le reste des données du back — contient l'heure actuelle : on ne
   * présume plus de bornes fixes côté IHM (elles peuvent différer de celles réellement
   * envoyées par le back, ex. plages calées sur 2h/8h/14h/20h heure de Paris plutôt que
   * 0h/6h/12h/18h, ce qui faisait échouer la comparaison par libellé et retombait à tort
   * sur la première plage de la liste). Pour hier (pas de « maintenant » pertinent), on
   * garde le libellé par défaut habituel, sinon la première plage fournie par le back.
   */
  private loadRanges(day: HistoryDay): void {
    this.monitoring.getHistoryRanges(this.stationId(), day).subscribe((ranges) => {
      this.ranges.set(ranges);
      const selected = this.defaultRangeFor(ranges, day);
      this.selectedRange.set(selected?.label ?? '');
      if (selected) {
        this.loadHistoryForRange(selected);
      } else {
        this.history.set([]);
      }
    });
  }

  private defaultRangeFor(ranges: HistoryRange[], day: HistoryDay): HistoryRange | undefined {
    const current = currentRangeFor(ranges, day);
    if (current) {
      return current;
    }
    const defaultLabel = defaultRangeLabelForDay(day);
    return ranges.find((r) => r.label === defaultLabel) ?? ranges[0];
  }

  private loadHistoryForRange(range: HistoryRange): void {
    this.monitoring
      .getStationHistory(this.stationId(), range, this.selectedDay())
      .subscribe((slots) => {
        this.history.set(slots);
        this.selectedMinutes.set(defaultHistorySlotMinutes(slots));
      });
  }

  selectSlot(slot: StationHistorySlot): void {
    // Vert ET gris sont cliquables : le gris peut signaler un DAT qui ne remonte plus
    // de données (déconnecté) et il faut pouvoir voir le détail par DAT à ce créneau.
    this.selectedMinutes.set(slot.minutes);
    this.goToDats(slot);
  }

  goToDats(slot: StationHistorySlot): void {
    this.router.navigate(['/gare', this.stationId(), 'dats'], {
      queryParams: { time: slot.minutes, day: this.selectedDay() },
    });
  }

  goBack(): void {
    this.router.navigate(['/']);
  }
}
